import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import CommonModal from "../../../components/shared/modalLayout/CommonModal";
import InputWithLabel from "../../../components/shared/fields/InputWithLabel";
import Button from "../../../components/shared/buttons/button";
import useTranslator from "../../../hooks/useTranslator";
import { useCreateBaggingRecordMutation, useUpdateBaggingRecordMutation } from "../rework/reworkApi";
import { convertToUserTimezone, getCurrentDateTime } from "../../../helpers/Utils";

const getInitialForm = () => ({
  start_time: getCurrentDateTime("YYYY-MM-DDTHH:mm"),
  end_time: getCurrentDateTime("YYYY-MM-DDTHH:mm"),
  cheeks_weight_before: "",
  cheeks_weight_after: "",
  strips_weight_before: "",
  strips_weight_after: "",
  total_weight_before: "",
  total_weight_after: "",
  total_evaporation: "",
});

const BaggingRecordModal = ({ show, setShow, record, mode, data_type }) => {
  const { _t } = useTranslator();
  const [formData, setFormData] = useState(getInitialForm);
  const [createBaggingRecord, { isLoading: isCreating }] = useCreateBaggingRecordMutation();
  const [updateBaggingRecord, { isLoading: isUpdating }] = useUpdateBaggingRecordMutation();

  useEffect(() => {
    if (record && mode === "edit") {
      setFormData({
        start_time:           record.start_time ? convertToUserTimezone(record.start_time, "YYYY-MM-DDTHH:mm") : getCurrentDateTime("YYYY-MM-DDTHH:mm"),
        end_time:             record.end_time   ? convertToUserTimezone(record.end_time,   "YYYY-MM-DDTHH:mm") : getCurrentDateTime("YYYY-MM-DDTHH:mm"),
        cheeks_weight_before: record.cheeks_weight_before || "",
        cheeks_weight_after:  record.cheeks_weight_after  || "",
        strips_weight_before: record.strips_weight_before || "",
        strips_weight_after:  record.strips_weight_after  || "",
        total_weight_before:  record.total_weight_before  || "",
        total_weight_after:   record.total_weight_after   || "",
        total_evaporation:    record.total_evaporation    || "",
      });
    } else {
      setFormData(getInitialForm());
    }
  }, [record, mode, show]);

  // auto-calc total_weight_before and total_weight_after
  useEffect(() => {
    const before = (parseFloat(formData.cheeks_weight_before) || 0) + (parseFloat(formData.strips_weight_before) || 0);
    const after  = (parseFloat(formData.cheeks_weight_after)  || 0) + (parseFloat(formData.strips_weight_after)  || 0);
    const evap   = before > 0 ? parseFloat(((before - after) / before * 100).toFixed(2)) : 0;
    setFormData((p) => ({ ...p, total_weight_before: before || "", total_weight_after: after || "", total_evaporation: evap || "" }));
  }, [formData.cheeks_weight_before, formData.cheeks_weight_after, formData.strips_weight_before, formData.strips_weight_after]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const handleClose = () => setShow(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isCreating || isUpdating) return;
    const payload = {
      ...formData,
      data_type,
      cheeks_weight_before: parseFloat(formData.cheeks_weight_before) || 0,
      cheeks_weight_after:  parseFloat(formData.cheeks_weight_after)  || 0,
      strips_weight_before: parseFloat(formData.strips_weight_before) || 0,
      strips_weight_after:  parseFloat(formData.strips_weight_after)  || 0,
      total_weight_before:  parseFloat(formData.total_weight_before)  || 0,
      total_weight_after:   parseFloat(formData.total_weight_after)   || 0,
      total_evaporation:    parseFloat(formData.total_evaporation)    || 0,
    };
    try {
      if (mode === "edit") {
        await updateBaggingRecord({ id: record.id, requestBody: payload }).unwrap();
      } else {
        await createBaggingRecord(payload).unwrap();
      }
      handleClose();
    } catch (error) {
      console.error("Error saving bagging record:", error);
    }
  };

  return (
    <CommonModal
      show={show}
      handleClose={handleClose}
      setShow={setShow}
      title={mode === "edit" ? _t("edit_bagging_record", "Edit Bagging Record") : _t("add_bagging_record", "Add Bagging Record")}
    >
      <Row>
        <Col lg={6}>
          <InputWithLabel label={_t("start_time", "Start Time")} type="datetime-local" name="start_time" value={formData.start_time} onChange={handleChange} />
        </Col>
        <Col lg={6}>
          <InputWithLabel label={_t("end_time", "End Time")} type="datetime-local" name="end_time" value={formData.end_time} onChange={handleChange} />
        </Col>
        <Col lg={6}>
          <InputWithLabel label={_t("cheeks_weight_before", "Cheeks Weight Before")} type="number" name="cheeks_weight_before" value={formData.cheeks_weight_before} onChange={handleChange} placeholder="0" />
        </Col>
        <Col lg={6}>
          <InputWithLabel label={_t("cheeks_weight_after", "Cheeks Weight After")} type="number" name="cheeks_weight_after" value={formData.cheeks_weight_after} onChange={handleChange} placeholder="0" />
        </Col>
        <Col lg={6}>
          <InputWithLabel label={_t("strips_weight_before", "Strips Weight Before")} type="number" name="strips_weight_before" value={formData.strips_weight_before} onChange={handleChange} placeholder="0" />
        </Col>
        <Col lg={6}>
          <InputWithLabel label={_t("strips_weight_after", "Strips Weight After")} type="number" name="strips_weight_after" value={formData.strips_weight_after} onChange={handleChange} placeholder="0" />
        </Col>
        <Col lg={6}>
          <InputWithLabel label={_t("total_weight_before", "Total Weight Before")} type="number" name="total_weight_before" value={formData.total_weight_before} onChange={handleChange} disabled={true} placeholder="0" />
        </Col>
        <Col lg={6}>
          <InputWithLabel label={_t("total_weight_after", "Total Weight After")} type="number" name="total_weight_after" value={formData.total_weight_after} onChange={handleChange} disabled={true} placeholder="0" />
        </Col>
        <Col lg={12}>
          <InputWithLabel label={_t("total_evaporation", "Total Evaporation (%)")} type="number" name="total_evaporation" value={formData.total_evaporation} onChange={handleChange} disabled={true} placeholder="0" />
        </Col>
      </Row>
      <Row>
        <Col className="text-end">
          <Button label={_t("save_changes", "Save Changes")} size="small" onClick={handleSubmit} disabled={isCreating || isUpdating} />
        </Col>
      </Row>
    </CommonModal>
  );
};

export default BaggingRecordModal;
